import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { usePipedriveStore, usePopupStore } from "../..";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import ILeaseComp from "../../interfaces/ILeaseComp";
import PopupType from "../../interfaces/PopupType";
import type PipedriveStore from "../../store/PipedriveStore";
import { leaseCompPropertyTypeToString, leaseCompSourceToString, leaseTypeToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import Grid, { IHeader } from "../Grid";

function LeaseCompsGrid({ leaseComps, additionalHeaders, radiusDropdown }: ILeaseCompsGridProps) {
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const navigateToLeaseComp = useNavigateToLeaseComp();
    const navigateToCompany = useNavigateToCompany();
    const headers = useMemo(() => buildLeaseCompsHeaders(pipedriveStore, navigateToCompany, additionalHeaders), [pipedriveStore, navigateToCompany, additionalHeaders],);
    const downloadHeaders = useMemo(() => buildLeaseCompsDownloadHeaders(headers), [headers],);

    return (
        <Grid
            data={leaseComps}
            headers={headers}
            downloadHeaders={downloadHeaders}
            getId={item => item.id}
            downloadFileName={"Lease Comps"}
            onRowClick={item => navigateToLeaseComp(item.id)}
            isRowActive={item => popupStore.type === PopupType.LeaseComp && popupStore.id === item.id}
            header={
                <div className={[commonStyles.gridTitle, commonStyles.header].join(" ")}>
                    {leaseComps.length} Lease Comps
                    {radiusDropdown}
                </div>
            }
        />
    );
}

export default observer(LeaseCompsGrid);

interface ILeaseCompsGridProps {
    leaseComps: ILeaseComp[];
    additionalHeaders?: IHeader<ILeaseComp>[];
    radiusDropdown?: React.ReactNode;
}

const buildLeaseCompsHeaders = (pipedriveStore: PipedriveStore, navigateToCompany: (id: number) => void, additionalHeaders?: IHeader<ILeaseComp>[]): IHeader<ILeaseComp>[] => {
    return [
        {
            align: "left",
            titleStrings: ["TENANT"],
            getValue: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveCompanyId)?.name || "",
            getLabel: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveCompanyId)?.name || "",
            onClick: item => item.pipedriveCompanyId && navigateToCompany(item.pipedriveCompanyId),
        },
        {
            align: "left",
            isFullWidth: true,
            titleStrings: ["ADDRESS"],
            getValue: item => item.address,
            getLabel: item => item.address,
        },
        {
            titleStrings: ["ZONING"],
            getValue: item => item.zoning,
            getLabel: item => item.zoning || "",
        },
        {
            titleStrings: ["BUILDING", "SIZE"],
            getValue: item => item.buildingSize,
            getLabel: item => item.buildingSize ? formatNumber(item.buildingSize) : "",
        },
        {
            titleStrings: ["TENANT", "LAND SIZE"],
            getValue: item => item.tenantLandSize,
            getLabel: item => item.tenantLandSize ? formatNumber(item.tenantLandSize) : "",
        },
        {
            titleStrings: ["ANNUAL", "RENT"],
            getValue: item => item.totalRent,
            getLabel: item => item.totalRent ? formatPrice(item.totalRent) : "",
        },
        {
            titleStrings: ["DATE", "SIGNED"],
            getValue: item => item.signedDate,
            getLabel: item => item.signedDate ? formatDate(item.signedDate) : "",
        },
        {
            titleStrings: ["EXPIRATION"],
            getValue: item => item.leaseExpiration,
            getLabel: item => item.leaseExpiration ? formatDate(item.leaseExpiration) : "",
        },
        {
            titleStrings: ["MONTHLY", "RENT"],
            getValue: item => item.totalRent,
            getLabel: item => item.totalRent ? formatPrice(item.totalRent / 12) : "",
        },
        {
            titleStrings: ["LAND", "$/ACRE"],
            getValue: item => item.landPsf,
            getLabel: item => item.landPsf ? formatPrice(item.landPsf) : "",
        },
        {
            titleStrings: ["BUILDING", "$/PSF"],
            getValue: item => item.buildingPsf,
            getLabel: item => item.buildingPsf ? formatPrice(item.buildingPsf) : "",
        },
        ...(additionalHeaders ?? []),
    ];
};

const buildLeaseCompsDownloadHeaders = (headers: IHeader<ILeaseComp>[]): IHeader<ILeaseComp>[] => {
    return [
        ...headers,
        {
            titleStrings: ["ID"],
            getValue: item => item.id,
            getLabel: item => String(item.id),
        },
        {
            titleStrings: ["PIPEDRIVE", "COMPANY ID"],
            getValue: item => item.pipedriveCompanyId,
            getLabel: item => item.pipedriveCompanyId === null || item.pipedriveCompanyId === undefined ? "" : String(item.pipedriveCompanyId),
        },
        {
            titleStrings: ["REGRID ID"],
            getValue: item => item.regridId,
            getLabel: item => item.regridId || "",
        },
        {
            titleStrings: ["PROPERTY TYPE"],
            getValue: item => item.propertyType,
            getLabel: item => item.propertyType ? leaseCompPropertyTypeToString(item.propertyType) : "",
        },
        {
            titleStrings: ["PARCEL SIZE", "(ACRES)"],
            getValue: item => item.parcelSizeAcres,
            getLabel: item => item.parcelSizeAcres === null || item.parcelSizeAcres === undefined ? "" : formatNumber(item.parcelSizeAcres),
        },
        {
            titleStrings: ["OPTIONS"],
            getValue: item => item.options,
            getLabel: item => item.options || "",
        },
        {
            titleStrings: ["NOTES"],
            getValue: item => item.notes,
            getLabel: item => item.notes || "",
        },
        {
            titleStrings: ["LEASE TYPE"],
            getValue: item => item.type,
            getLabel: item => item.type ? leaseTypeToString(item.type) : "",
        },
        {
            titleStrings: ["YEAR"],
            getValue: item => item.year,
            getLabel: item => item.year === null || item.year === undefined ? "" : String(item.year),
        },
        {
            titleStrings: ["FREE RENT"],
            getValue: item => item.freeRent,
            getLabel: item => item.freeRent || "",
        },
        {
            titleStrings: ["TI ALLOWANCE"],
            getValue: item => item.ti,
            getLabel: item => item.ti === null || item.ti === undefined ? "" : formatPrice(item.ti),
        },
        {
            titleStrings: ["ESCALATIONS"],
            getValue: item => item.escalations,
            getLabel: item => item.escalations || "",
        },
        {
            titleStrings: ["CENTER LATITUDE"],
            getValue: item => item.centerPoint?.latitude,
            getLabel: item => item.centerPoint?.latitude === null || item.centerPoint?.latitude === undefined ? "" : String(item.centerPoint?.latitude),
        },
        {
            titleStrings: ["CENTER LONGITUDE"],
            getValue: item => item.centerPoint?.longitude,
            getLabel: item => item.centerPoint?.longitude === null || item.centerPoint?.longitude === undefined ? "" : String(item.centerPoint?.longitude),
        },
        {
            titleStrings: ["MODIFIED BY"],
            getValue: item => item.modifiedByUser,
            getLabel: item => item.modifiedByUser || "",
        },
        {
            titleStrings: ["MODIFIED DATE"],
            getValue: item => item.modifiedDate,
            getLabel: item => item.modifiedDate ? formatDateTime(item.modifiedDate) : "",
        },
        {
            titleStrings: ["COMP SOURCE"],
            getValue: item => item.compSource,
            getLabel: item => item.compSource ? leaseCompSourceToString(item.compSource) : "",
        },
    ];
};
